import React from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, AlertTriangle, CreditCard, Scale } from 'lucide-react';

interface LegalSubsection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
}

interface LegalSection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
  note?: string;
  highlighted?: boolean;
  muted?: boolean;
  subsections?: LegalSubsection[];
}

// Icons displayed next to specific article headings, matched by index (0-based).
const HEADING_ICONS: Record<number, React.ReactNode> = {
  3: <AlertTriangle className="h-5 w-5 text-destructive" />,
  4: <CreditCard className="h-4 w-4 text-primary" />,
  7: <Shield className="h-4 w-4 text-primary" />,
};

const CGVPage = () => {
  const { t, i18n } = useTranslation('legal');
  const sections = t('cgv.sections', { returnObjects: true }) as LegalSection[];

  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold text-center mb-8">{t('cgv.pageTitle')}</h1>

        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-primary" />
              {t('cgv.cardTitle')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{t('cgv.lastUpdate')}</p>
          </CardHeader>
          <CardContent className="space-y-8">
            {sections.map((section, index) => (
              <section
                key={index}
                className={
                  section.highlighted
                    ? 'border border-destructive/30 rounded-lg p-5'
                    : section.muted
                    ? 'bg-muted/30 p-6 rounded-lg'
                    : undefined
                }
              >
                <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  {HEADING_ICONS[index]}
                  {section.heading}
                </h2>

                <div className={section.highlighted ? 'space-y-4 text-muted-foreground' : undefined}>
                  {section.paragraphs?.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className={
                        section.highlighted
                          ? undefined
                          : `text-muted-foreground ${pIndex < (section.paragraphs?.length ?? 0) - 1 || section.items ? 'mb-2' : ''}`
                      }
                      dangerouslySetInnerHTML={{ __html: paragraph }}
                    />
                  ))}

                  {section.items && (
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                      {section.items.map((item, iIndex) => (
                        <li key={iIndex} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  )}

                  {section.note && (
                    <p
                      className="text-sm text-muted-foreground mt-3 bg-muted/50 p-3 rounded-lg"
                      dangerouslySetInnerHTML={{ __html: section.note }}
                    />
                  )}

                  {section.subsections?.map((sub, sIndex) => (
                    <div key={sIndex}>
                      <h3 className="font-semibold text-foreground">{sub.heading}</h3>
                      {sub.paragraphs?.map((paragraph, pIndex) => (
                        <p key={pIndex} dangerouslySetInnerHTML={{ __html: paragraph }} />
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {!i18n.language.startsWith('fr') && (
              <p className="text-xs text-muted-foreground">{t('prevailNotice')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CGVPage;
